import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { MarketingLayout } from "../../components/marketing/MarketingLayout";
import { MarketingCTA } from "../../components/marketing/MarketingCTA";
import { ArticleBody } from "../../components/marketing/ArticleBody";
import { SEO } from "../../components/seo/SEO";
import { breadcrumbSchema } from "../../components/seo/schema";
import { getMarketingIcon } from "../../data/marketingIcons";
import { FEATURES_BY_SLUG } from "../../data/featuresData";
import { INDUSTRIES_BY_SLUG } from "../../data/industriesData";
import { resourcesApi } from "../../lib/api";
import { Link } from "react-router-dom";
import NotFound from "../NotFound";

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(undefined);

  useEffect(() => {
    setArticle(undefined);
    resourcesApi
      .get(slug)
      .then(setArticle)
      .catch(() => setArticle(null));
  }, [slug]);

  if (article === undefined) {
    return (
      <MarketingLayout>
        <div className="flex justify-center py-24" data-testid="article-loading">
          <Loader2 className="h-6 w-6 text-slate-500 animate-spin" />
        </div>
      </MarketingLayout>
    );
  }
  if (article === null) return <NotFound />;

  const categoryLabel = article.category === "guide" ? "Guides" : article.category === "tool" ? "Tools" : "Blog";
  const categoryPath = `/resources/${article.category === "guide" ? "guides" : article.category === "tool" ? "tools" : "blog"}`;
  const breadcrumb = [
    { name: "Home", path: "/" },
    { name: "Resources", path: "/resources" },
    { name: categoryLabel, path: categoryPath },
    { name: article.title, path: `${categoryPath}/${article.slug}` },
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.h1,
    description: article.metaDescription,
    image: article.ogImage,
    author: { "@type": "Organization", name: article.authorName },
    datePublished: article.publishedAt,
    dateModified: article.updatedAt,
  };

  return (
    <MarketingLayout breadcrumb={breadcrumb}>
      <SEO
        title={article.seoTitle}
        description={article.metaDescription}
        path={`${categoryPath}/${article.slug}`}
        robots={article.indexable ? "index, follow" : "noindex, follow"}
        ogImage={article.ogImage}
        ogType="article"
        jsonLd={[breadcrumbSchema(breadcrumb), articleSchema]}
      />
      <article className="max-w-2xl mx-auto px-5 lg:px-8 pt-10 pb-16">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight leading-[1.1] mb-4" data-testid="article-h1">
          {article.h1}
        </h1>
        <div className="flex items-center gap-2 text-[12.5px] text-slate-500 mb-8">
          <span>{article.authorName}</span>
          <span>·</span>
          <time dateTime={article.publishedAt}>
            {new Date(article.publishedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
          </time>
        </div>
        {article.featuredImage && (
          <img
            src={article.featuredImage}
            alt={article.h1}
            className="w-full h-64 sm:h-80 object-cover rounded-2xl mb-10"
            loading="eager"
          />
        )}
        <ArticleBody content={article.content} />

        {(article.relatedFeatures?.length > 0 || article.relatedIndustries?.length > 0) && (
          <div className="mt-12 pt-8 border-t border-white/10 grid grid-cols-1 sm:grid-cols-2 gap-8">
            {article.relatedFeatures?.length > 0 && (
              <div>
                <h3 className="text-[12px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Related features</h3>
                <div className="flex flex-wrap gap-2">
                  {article.relatedFeatures.map((s) => {
                    const f = FEATURES_BY_SLUG[s];
                    if (!f) return null;
                    const Icon = getMarketingIcon(f.icon);
                    return (
                      <Link key={s} to={`/features/${s}`} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-[12.5px] text-slate-300 hover:text-white hover:border-blue-400 transition-colors">
                        <Icon className="h-3.5 w-3.5" /> {f.title}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
            {article.relatedIndustries?.length > 0 && (
              <div>
                <h3 className="text-[12px] uppercase tracking-widest text-slate-500 font-semibold mb-3">Related industries</h3>
                <div className="flex flex-wrap gap-2">
                  {article.relatedIndustries.map((s) => {
                    const ind = INDUSTRIES_BY_SLUG[s];
                    if (!ind) return null;
                    return (
                      <Link key={s} to={`/industries/${s}`} className="px-3 py-1.5 rounded-full border border-white/10 text-[12.5px] text-slate-300 hover:text-white hover:border-amber-400 transition-colors">
                        {ind.title}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {article.authorBio && (
          <div className="mt-10 pt-6 border-t border-white/10 text-[13px] text-slate-500 leading-relaxed">
            <span className="text-slate-400 font-semibold">{article.authorName}</span> — {article.authorBio}
          </div>
        )}
      </article>
      <MarketingCTA heading="See it running in your own business" sub="Start your 14-day free trial — no credit card required." />
    </MarketingLayout>
  );
}
